import {Stack, Typography, Alert} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import MainBtn from '../customTable/MainBtn'
import PopUp from '../customTable/PopUp'
import ShowPlan from './ShowPlan'
import {useNavigate} from 'react-router-dom'
import PackagesApi from '../../Api/Packages'
import {useSelector} from 'react-redux'
import {RootState} from '../../store'
import {
  formatPaymentDisplay,
  handlePaymentInitiationResponse,
  parsePaymentAmount,
  resolveLocalizedString,
  isBlomUnifiedCheckout,
  runBlomUnifiedCheckout,
  toIsoCurrencyCode,
} from '../../utils/paymentHelpers'
import PaymentGatewaySelector from '../wallet/components/PaymentGatewaySelector'
import OTPModal from '../wallet/components/OTPModal'

type FlowStep = 'otp' | 'gateway'

export default function Cards({data, setData}: any) {
  const navigate = useNavigate()
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [flowOpen, setFlowOpen] = useState(false)
  const [flowStep, setFlowStep] = useState<FlowStep>('otp')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [signature, setSignature] = useState('')
  const [renewAmount, setRenewAmount] = useState(0)
  const [renewCurrency, setRenewCurrency] = useState('USD')
  const [otpAmountLabel, setOtpAmountLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const store = useSelector((state: RootState) => state.vender)

  const countryId = Number(localStorage.getItem('country_id') || 0)
  const planName = resolveLocalizedString(data.plan_name, store.lang, store.defLang)

  const resetFlow = () => {
    setFlowStep('otp')
    setSignature('')
    setErrorMsg('')
    setOtpAmountLabel('')
  }

  const closeFlow = () => {
    if (isLoading) {
      return
    }
    setFlowOpen(false)
    resetFlow()
  }

  const openRenewCheckout = () => {
    setErrorMsg('')
    PackagesApi.renew((res: any) => {
      const amount = parsePaymentAmount(res?.amount ?? res?.data?.amount ?? data?.payment_details?.total)
      const currency = String(
        res?.currency ?? res?.data?.currency ?? localStorage.getItem('currency') ?? 'USD'
      ).trim()
      const nextSignature = res?.signature ?? res?.data?.signature

      if (!nextSignature || amount <= 0) {
        setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
        return
      }

      setRenewAmount(amount)
      setRenewCurrency(currency)
      setSignature(String(nextSignature))
      setOtpAmountLabel(formatPaymentDisplay(amount, currency))
      setData({
        ...data,
        payment_details: {
          ...data.payment_details,
          amount,
          currency,
          total: formatPaymentDisplay(amount, currency),
        },
      })
      setFlowStep('otp')
      setFlowOpen(true)
    }, setIsLoading)
  }

  const handleOtpSubmit = (code: string) => {
    setErrorMsg('')
    PackagesApi.renewConfirm(
      {signature, code},
      () => setFlowStep('gateway'),
      setIsLoading
    )
  }

  const handleGatewaySelected = (factoryName: string) => {
    const currency_code = toIsoCurrencyCode(renewCurrency)

    if (isBlomUnifiedCheckout(factoryName)) {
      runBlomUnifiedCheckout(
        {
          purpose: 'subscription',
          process: 'renew',
          currency_code,
        },
        setIsLoading
      )
      return
    }

    PackagesApi.payByGateway(
      factoryName,
      {process: 'renew', currency_code},
      (res: any) => {
        handlePaymentInitiationResponse(res)
      },
      setIsLoading
    )
  }

  return (
    <Stack mt='30px'>
      <Stack direction={{sm: 'row', xs: 'column'}} gap={'24px'}>
        <Stack
          sx={{
            background: 'var(--bs-app-footer-bg-color)',
            width: {xs: '100%'},
            borderRadius: '8px',
            padding: '24px',
            gap: '24px',
          }}
        >
          <Typography sx={{fontSize: '16px', fontWeight: '600'}}>{t('accountDetails')}</Typography>
          <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
            {t('sellerName')} : <span style={{color: '#787887'}}>{data.vendor_name}</span>
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
            {t('mobileNumber')} :{' '}
            <span dir='ltr' style={{color: '#787887'}}>
              {data.vendor_mobile}
            </span>
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
            {t('sellerEmail')} : <span style={{color: '#787887'}}>{data.vendor_email}</span>
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
            {t('country')} :{' '}
            <span style={{color: '#787887'}}>
              {resolveLocalizedString(data.country_name, store.lang, store.defLang)}
            </span>
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
            {t('city')} :{' '}
            <span style={{color: '#787887'}}>
              {resolveLocalizedString(data.city_name, store.lang, store.defLang)}
            </span>
          </Typography>
        </Stack>
      </Stack>
      <Stack mt='24px'>
        <Stack
          sx={{
            background: 'var(--bs-app-footer-bg-color)',
            width: {xs: '100%', sm: '100%'},
            borderRadius: '8px',
            padding: '24px',
            gap: '24px',
          }}
        >
          <Typography sx={{fontSize: '16px', fontWeight: '600'}}>
            {t('subscriptionsDetails')}
          </Typography>
          <Stack
            flexWrap={'wrap'}
            justifyContent={'space-between'}
            direction={{sm: 'row', xs: 'column'}}
            gap={'24px'}
          >
            <Stack gap='24px'>
              <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
                {t('planName')} : <span style={{color: '#009EF7'}}>{planName} </span>
              </Typography>{' '}
              <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
                {t('subscriptionStatus')} :{' '}
                <span style={{color: '#787887'}}>{data.status_text} </span>
              </Typography>{' '}
              <MainBtn
                secondary
                width='max-content'
                text='planFeatures'
                handelSubmit={() => {
                  navigate('/subscriptions-change-package')
                }}
              />
              <PopUp setOpen={setOpen} open={open} width={'md'}>
                <ShowPlan data={data.features} />
              </PopUp>
            </Stack>

            <Stack minWidth={'350px'} gap='24px'>
              <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
                {t('startDate')} :{' '}
                <span dir='ltr' style={{color: '#787887'}}>
                  {data.start_at}{' '}
                </span>
              </Typography>{' '}
              <Typography sx={{fontSize: '16px', fontWeight: '500'}}>
                {t('endDate')} :{' '}
                <span dir='ltr' style={{color: '#787887'}}>
                  {data.is_limited ? data.expire_at : t('unlimited')}
                </span>
              </Typography>{' '}
            </Stack>
          </Stack>
          <Stack
            gap='20px'
            direction={'row'}
            flexWrap={'wrap'}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
          >
            <MainBtn
              handelSubmit={() => {
                navigate('/subscriptions-change-package')
              }}
              width='auto'
              text='upgradeSubscriptionPackage'
            />{' '}
            {store.permissions && store.permissions.includes('vendor-subscription_update') && (
              <>
                {data.type !== 'free' && (
                  <MainBtn
                    without
                    handelSubmit={openRenewCheckout}
                    width='auto'
                    text='renew'
                    isLoading={isLoading}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Stack>

      <PopUp isLoading={isLoading} width='sm' open={flowOpen} setOpen={setFlowOpen}>
        <Stack p={{xs: '16px', sm: '24px'}} gap='12px' bgcolor='#fff'>
          {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}
          {flowStep === 'otp' ? (
            <OTPModal
              signature={signature}
              amountLabel={otpAmountLabel}
              amount={renewAmount}
              currency={renewCurrency}
              planName={planName}
              isLoading={isLoading}
              isResending={isResending}
              onSubmit={handleOtpSubmit}
              onCancel={closeFlow}
              onResend={() =>
                PackagesApi.resendOtp({signature}, () => {}, setIsResending)
              }
            />
          ) : (
            <PaymentGatewaySelector
              open={flowOpen && flowStep === 'gateway'}
              onClose={closeFlow}
              amount={renewAmount}
              currency={renewCurrency}
              purpose='subscription'
              countryId={countryId}
              referenceId={Number(data.subscription_id) || 0}
              isPaying={isLoading}
              onGatewaySelected={handleGatewaySelected}
              summary={{
                titleKey: 'completePayment',
                planName,
                priceLabel: formatPaymentDisplay(renewAmount, renewCurrency),
              }}
            />
          )}
        </Stack>
      </PopUp>
    </Stack>
  )
}
